import { Update } from '@mui/icons-material';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import { getConsultorResponse } from 'lib/api';
import { getBuroByDate, getBuroHistory } from 'lib/api/integrations/buro';
import CustomButton from 'lib/designSystem/Button';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import HeaderWithActions from 'lib/designSystem/HeaderWithActions';
import {
  formatDate,
  showAlert,
  validatePermissions,
} from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { AuthState } from 'services/redux/authSlice';

import CompanyReport from '../../../../Consultor/ResponseTypes/Buro/components/CompanyReport';
import PersonReport from '../../../../Consultor/ResponseTypes/Buro/components/PersonReport';

const Buro = (props: { businessId?: string | null }) => {
  const dispatch = useDispatch();
  const { businessId } = props;
  const { id } = useParams<{
    id: string;
    businessId: string;
    page: string;
  }>();
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState<string[]>([]);
  const [lastUpdate, setLastUpdate] = useState<Date | undefined>(undefined);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [buro, setBuro] = useState<object | null>(null);
  const [isPerson, setIsPerson] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);

  const businessIdentifier = id ?? businessId;

  const fetchHistory = async () => {
    try {
      if (!businessIdentifier) return;
      const history = await getBuroHistory(dispatch, businessIdentifier);
      setHistory(history.data);
      setLastUpdate(new Date(history.lastUpdate));
      if (history.data && history.data.length > 0) {
        const firstDate = history.data[0];
        setSelectedDate(firstDate);
        void fetchBuroByDate(firstDate);
      }
    } catch (error) {
      console.error(error);
      showAlert(dispatch, 'error', 'Ha ocurrido un error');
    } finally {
      setLoading(false);
    }
  };

  const fetchBuroByDate = async (date?: string) => {
    try {
      setLoading(true);
      const dateToUse = date ?? selectedDate;
      if (!dateToUse || !businessIdentifier) return;
      const buro = await getBuroByDate(dispatch, businessIdentifier, dateToUse);
      setBuro(buro);
      setIsPerson(buro.isPerson);
    } catch (error) {
      console.error(error);
      showAlert(dispatch, 'error', 'Ha ocurrido un error');
    } finally {
      setLoading(false);
    }
  };

  const fetchBuro = async () => {
    setLoading(true);
    try {
      if (!businessIdentifier) return;
      const responseConsult = await getConsultorResponse(dispatch, {
        type: 'rut',
        consult: 'buro',
        payload: { identifier: businessIdentifier },
      });
      setBuro(responseConsult.data);
      setIsPerson(responseConsult.data.isPerson);
      setLastUpdate(new Date());
      const history = await getBuroHistory(dispatch, businessIdentifier);
      setHistory(history.data);
      setSelectedDate(history.data[0]);
      setOpen(false);
    } catch (error) {
      console.error(error);
      showAlert(dispatch, 'error', 'Ha ocurrido un error');
    } finally {
      setLoading(false);
    }
  };

  const canUpdate = () => validatePermissions(user, 'can_update_buro');

  const handleDateChange = (event: SelectChangeEvent<string>) => {
    const newDate = event.target.value;
    setSelectedDate(newDate);
    void fetchBuroByDate(newDate);
  };

  useEffect(() => {
    void fetchHistory();
  }, []);

  if (loading) return <CenteredCircularProgress relative />;

  return (
    <div>
      <HeaderWithActions
        item={
          <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography>
              {lastUpdate
                ? `Última actualización: ${formatDate(lastUpdate, 'dd-mm-yyyy')}`
                : 'No se ha actualizado el buró de crédito'}
            </Typography>
            <IconButton
              disabled={!canUpdate()}
              onClick={() => setOpen(true)}
              sx={{ marginLeft: 1 }}
            >
              <Update />
            </IconButton>
          </div>
        }
        title={
          history.length > 0 && (
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Seleccionar fecha</InputLabel>
              <Select
                value={selectedDate ?? ''}
                label="Seleccionar fecha"
                onChange={handleDateChange}
              >
                {history.map((date) => (
                  <MenuItem key={date} value={date}>
                    {date}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )
        }
        subHeader
        variant="body1"
      />
      <div
        style={
          businessId
            ? { maxHeight: '400px', overflow: 'auto' }
            : { paddingBottom: '20px' }
        }
      >
        {buro && (
          <div>
            {isPerson ? (
              <PersonReport response={buro} lastUpdate={lastUpdate} />
            ) : (
              <CompanyReport response={buro} lastUpdate={lastUpdate} />
            )}
          </div>
        )}
      </div>

      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>Actualizar buró de crédito</DialogTitle>
        <DialogContent>
          <Typography>
            Recuerda que esta acción consume créditos de tu plan.
          </Typography>
        </DialogContent>
        <DialogActions>
          <CustomButton color="secondary" onClick={() => setOpen(false)}>
            Cancelar
          </CustomButton>
          <CustomButton onClick={() => fetchBuro()}>Actualizar</CustomButton>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Buro;
